<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>
  <p-toast position="top-center"></p-toast>

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'Lot_Item_Relocation_Title' | translateWF: 'Relocate Lot Items' }}</h1>

        <div class="page__title_buttons">
          <div class="relocation__tabs">
            <button type="button" class="relocation__tab relocation__tab--active">
              {{ 'Lot_Item_Relocation_Tab_Relocate' | translateWF: 'Relocate' }}
            </button>
            <button type="button" class="relocation__tab" (click)="redirectToHistory()">
              {{ 'Lot_Item_Relocation_Tab_History' | translateWF: 'Movement history' }}
              @if (historyCount() !== null) {
                <span>· {{ historyCount() }}</span>
              }
            </button>
          </div>
        </div>
      </div>

      <p class="page__subtitle">
        {{ 'Lot_Item_Relocation_Subtitle' | translateWF: 'Choose a warehouse, then scan rolls, chemicals or pallets and a destination in any order.' }}
      </p>
    </div>

    @if (mode() === 'WarehouseSelection') {
      <section class="relocation__card">
        <span class="relocation__step">{{ 'Lot_Item_Relocation_Step_One' | translateWF: 'STEP 1' }}</span>
        <h2 class="relocation__card_title">
          {{ 'Lot_Item_Relocation_Choose_Warehouse' | translateWF: 'Choose the warehouse you are working in' }}
        </h2>
        <p class="relocation__card_hint">
          {{ 'Lot_Item_Relocation_Same_Warehouse_Hint' | translateWF: 'Lot Items can only be relocated between locations of the same warehouse.' }}
        </p>

        <div class="relocation__warehouses">
          @for (warehouse of warehouseOptions(); track warehouse.locationCode) {
            <button type="button" class="relocation__warehouse" [disabled]="!canEdit() || !isSelectableWarehouse(warehouse)" (click)="selectWarehouse(warehouse)">
              <span class="relocation__warehouse_head">
                <i class="pi pi-building"></i>
                <span class="relocation__warehouse_code">{{ warehouse.code }}</span>
                <i class="pi pi-chevron-right relocation__warehouse_chevron"></i>
              </span>

              <span class="relocation__warehouse_name">{{ warehouse.name }}</span>

              <span class="relocation__warehouse_badges">
                <span class="relocation__badge">
                  {{ warehouse.storableLocationCount ?? '—' }}
                  {{ 'Lot_Item_Relocation_Storable_Locations' | translateWF: 'storable locations' }}
                </span>
                <span class="relocation__badge">
                  {{ warehouse.lotItemCount ?? '—' }}
                  {{ 'Lot_Item_Relocation_Lot_Items' | translateWF: 'Lot Items' }}
                </span>
              </span>

              @if (!isSelectableWarehouse(warehouse)) {
                <span class="relocation__warehouse_reason">
                  <i class="pi pi-info-circle"></i>
                  {{ 'Lot_Item_Relocation_No_Storable_Locations' | translateWF: 'No storable locations to relocate to' }}
                </span>
              }
            </button>
          } @empty {
            <p class="relocation__empty">
              {{ 'Lot_Item_Relocation_No_Warehouses' | translateWF: 'No warehouses available in this site' }}
            </p>
          }
        </div>
      </section>
    } @else if (relocation()) {
      @let current = relocation()!;

      <section class="relocation__warehouse_bar">
        <span class="relocation__warehouse_bar_info">
          <i class="pi pi-building"></i>
          <span class="relocation__warehouse_bar_label">{{ 'Lot_Item_Relocation_Warehouse' | translateWF: 'WAREHOUSE' }}</span>
          <span class="relocation__warehouse_bar_code">{{ current.warehouseCode }}</span>
          <span class="relocation__warehouse_bar_name">{{ warehouseName() }}</span>
          <span class="relocation__warehouse_bar_number">{{ current.number }}</span>
        </span>

        @if (isDraft() && canEdit()) {
          <p-button
            icon="pi pi-refresh"
            severity="secondary"
            [outlined]="true"
            [label]="'Lot_Item_Relocation_Change_Warehouse' | translateWF: 'Change warehouse'"
            (onClick)="changeWarehouse()"
          />
        }
      </section>

      <div class="grid grid-cols-12 gap-[2rem]">
        <div class="col-span-12 xl:col-span-8">
          @if (isDraft()) {
            <section class="relocation__card mb-[2rem]">
              <span class="relocation__step">{{ 'Lot_Item_Relocation_Scan' | translateWF: 'SCAN' }}</span>

              <div class="relocation__scan">
                <button
                  type="button"
                  class="relocation__scan_qr"
                  [disabled]="!canScan()"
                  [title]="'Lot_Item_Relocation_Scan_Qr' | translateWF: 'Scan with camera'"
                  (click)="qrScanner.openQrScanner()"
                >
                  <i class="pi pi-qrcode"></i>
                </button>
                <input
                  #scanInput
                  type="text"
                  pInputText
                  class="relocation__scan_input"
                  autocomplete="off"
                  autocapitalize="off"
                  spellcheck="false"
                  [disabled]="!canScan()"
                  [placeholder]="'Lot_Item_Relocation_Scan_Placeholder' | translateWF: 'Scan Lot Item or location barcode'"
                  [(ngModel)]="scanText"
                  (keydown.enter)="onScanSubmit()"
                  (blur)="onScanBlur($event)"
                />
                <span class="relocation__scan_hint">{{ 'Lot_Item_Relocation_Enter' | translateWF: 'Enter' }}</span>
              </div>

              @if (quickScanCodes().length) {
                <div class="relocation__quick">
                  <span class="relocation__quick_label"> {{ 'Lot_Item_Relocation_Quick_Scan' | translateWF: 'Quick scan' }}: </span>

                  @for (code of quickScanCodes(); track code) {
                    <button type="button" class="relocation__quick_chip" [disabled]="!canScan()" (click)="onQuickScan(code)">
                      {{ code }}
                    </button>
                  }
                </div>
              }

              @if (pendingScans() > 0) {
                <p class="relocation__scan_pending">
                  <i class="pi pi-spin pi-spinner"></i>
                  {{ pendingScans() }} {{ 'Lot_Item_Relocation_Queued' | translateWF: 'queued' }}
                </p>
              }

              @if (lastScanOutcome(); as outcome) {
                <div class="relocation__outcome" [class]="'relocation__outcome--' + outcome.severity">
                  <strong>{{ outcome.title }}</strong>
                  @if (outcome.detail) {
                    <span>{{ outcome.detail }}</span>
                  }
                </div>
              }

              <app-qr-scanner-modal #qrScanner [showTrigger]="false" (isScannedCode)="onQrCodeScanned($event)" />
            </section>
          }

          <section class="relocation__card">
            <div class="relocation__collected_head">
              <h2 class="relocation__card_title">
                {{ 'Lot_Item_Relocation_Collected' | translateWF: 'Collected items' }}
              </h2>
              <span class="relocation__count">{{ lines().length }}</span>
            </div>

            @if (lines().length) {
              <p-table [value]="lines()" styleClass="mt-[1rem]">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ 'Lot_Item_Relocation_Lot_Item_Number' | translateWF: 'Lot Item' }}</th>
                    <th>{{ 'Lot_Item_Relocation_Type' | translateWF: 'Type' }}</th>
                    <th>{{ 'Lot_Item_Relocation_From' | translateWF: 'From location' }}</th>
                    <th>{{ 'Common_Status' | translateWF: 'Status' }}</th>
                    @if (!isDraft()) {
                      <th>{{ 'Lot_Item_Relocation_Result' | translateWF: 'Result' }}</th>
                    }
                    @if (isDraft() && canEdit()) {
                      <th></th>
                    }
                  </tr>
                </ng-template>

                <ng-template pTemplate="body" let-line>
                  <tr>
                    <td class="relocation__lot_number">{{ line.lotItemNumber }}</td>
                    <td>
                      <p-tag [value]="getLotItemTypeLabel(line.type)" severity="secondary" />
                    </td>
                    <td>{{ line.fromLocationPath ? locationPathToText(line.fromLocationPath) : line.fromLocationCode }}</td>
                    <td>
                      <p-tag
                        [value]="line.lineStatus === 1 ? ('Lot_Item_Relocation_Already_Here' | translateWF: 'Already here') : ('Lot_Item_Relocation_Ready' | translateWF: 'Ready')"
                        [severity]="line.lineStatus === 1 ? 'warn' : 'success'"
                      />
                    </td>
                    @if (!isDraft()) {
                      <td>
                        <p-tag
                          [value]="line.result === 1 ? ('Lot_Item_Relocation_Skipped' | translateWF: 'Skipped') : ('Lot_Item_Relocation_Moved' | translateWF: 'Moved')"
                          [severity]="line.result === 1 ? 'secondary' : 'success'"
                        />
                      </td>
                    }
                    @if (isDraft() && canEdit()) {
                      <td class="text-right">
                        <p-button icon="pi pi-times" severity="danger" [text]="true" (onClick)="removeLine(line.lotItemNumber)" />
                      </td>
                    }
                  </tr>
                </ng-template>
              </p-table>
            } @else {
              <div class="relocation__collected_empty">
                <i class="pi pi-box"></i>
                <p>{{ 'Lot_Item_Relocation_No_Items' | translateWF: 'No items yet. Scan a Lot Item barcode to start.' }}</p>
              </div>
            }
          </section>
        </div>

        <div class="col-span-12 xl:col-span-4">
          <section class="relocation__card mb-[2rem]">
            <h2 class="relocation__card_title">{{ 'Lot_Item_Relocation_Destination' | translateWF: 'Destination' }}</h2>
            <p class="relocation__card_hint">
              {{ 'Lot_Item_Relocation_Destination_Hint' | translateWF: 'Scan a location barcode, or search and pick below. Only storable locations can be selected.' }}
            </p>

            <p-iconfield iconPosition="left" class="relocation__destination_search">
              <p-inputicon styleClass="pi pi-search" />
              <input
                type="text"
                pInputText
                [placeholder]="'Lot_Item_Relocation_Search_Location' | translateWF: 'Search code or name'"
                [(ngModel)]="destinationFilter"
                (ngModelChange)="onDestinationFilterChange()"
              />
            </p-iconfield>

            <div class="relocation__tree">
              <ng-template #locationRows let-nodes>
                @for (node of nodes; track node.key) {
                  @if (isDestinationVisible(node)) {
                    <div class="relocation__tree_row" [class.relocation__tree_row--selected]="isSelectedDestination(node)">
                      @if (node.children?.length) {
                        <button
                          type="button"
                          class="relocation__tree_toggle"
                          [attr.aria-expanded]="isExpanded(node)"
                          [title]="isExpanded(node) ? ('Lot_Item_Relocation_Collapse' | translateWF: 'Collapse') : ('Lot_Item_Relocation_Expand' | translateWF: 'Expand')"
                          (click)="toggleNode(node)"
                        >
                          <i class="pi" [class.pi-chevron-down]="isExpanded(node)" [class.pi-chevron-right]="!isExpanded(node)"></i>
                        </button>
                      } @else {
                        <span class="relocation__tree_toggle_spacer"></span>
                      }

                      <button type="button" class="relocation__tree_label" [disabled]="!getLocation(node).isStorable || !isDraft() || !canEdit()" (click)="selectDestination(node)">
                        <span class="relocation__tree_code">{{ getLocation(node).code }}</span>
                        <span class="relocation__tree_name">{{ getLocationName(node) }}</span>
                        <span class="relocation__tree_type">{{ getLocationTypeLabel(node) }}</span>
                        @if (getLocation(node).isStorable) {
                          <i class="pi pi-check relocation__tree_ok"></i>
                        } @else {
                          <i class="pi pi-times relocation__tree_no"></i>
                        }
                      </button>
                    </div>

                    @if (node.children?.length && isExpanded(node)) {
                      <div class="relocation__tree_children">
                        <ng-container *ngTemplateOutlet="locationRows; context: { $implicit: node.children }" />
                      </div>
                    }
                  }
                }
              </ng-template>

              <ng-container *ngTemplateOutlet="locationRows; context: { $implicit: destinationTree() }" />

              @if (!destinationTree().length && !isLoadingDestinations()) {
                <p class="relocation__empty">
                  {{ 'Lot_Item_Relocation_No_Locations' | translateWF: 'No locations found for this warehouse' }}
                </p>
              }
            </div>
          </section>

          <section class="relocation__card">
            @if (isDraft()) {
              <div class="relocation__summary_row">
                <span>{{ 'Lot_Item_Relocation_Destination' | translateWF: 'Destination' }}</span>
                <strong>{{ current.toLocationCode || ('Lot_Item_Relocation_Destination_Not_Set' | translateWF: 'Not set') }}</strong>
              </div>
              <div class="relocation__summary_row">
                <span>{{ 'Lot_Item_Relocation_Ready_To_Move' | translateWF: 'Ready to move' }}</span>
                <strong>{{ readyCount() }} {{ 'Lot_Item_Relocation_Lot_Items' | translateWF: 'Lot Items' }}</strong>
              </div>
              <div class="relocation__summary_row">
                <span>{{ 'Lot_Item_Relocation_Skipped_Already' | translateWF: 'Skipped (already there)' }}</span>
                <strong>{{ alreadyHereCount() }}</strong>
              </div>

              <p-button
                styleClass="w-full mt-[1rem]"
                icon="pi pi-arrow-right"
                iconPos="right"
                [label]="'Lot_Item_Relocation_Move_Items' | translateWF: 'Move items'"
                [disabled]="!canConfirm()"
                (onClick)="confirmRelocation()"
              />

              @if (!lines().length) {
                <p class="relocation__summary_hint">
                  {{ 'Lot_Item_Relocation_Scan_At_Least_One' | translateWF: 'Scan at least one Lot Item.' }}
                </p>
              } @else if (!current.toLocationCode) {
                <p class="relocation__summary_hint">
                  {{ 'Lot_Item_Relocation_Set_Destination' | translateWF: 'Set a destination.' }}
                </p>
              } @else if (allLinesAlreadyHere()) {
                <p class="relocation__summary_hint">
                  {{ 'Lot_Item_Relocation_All_Already_There' | translateWF: 'All items are already at the destination. Change it or remove items.' }}
                </p>
              }
            } @else {
              <div class="relocation__summary_row">
                <span>{{ 'Lot_Item_Relocation_Moved' | translateWF: 'Moved' }}</span>
                <strong>{{ movedCount() }}</strong>
              </div>
              <div class="relocation__summary_row">
                <span>{{ 'Lot_Item_Relocation_Skipped' | translateWF: 'Skipped' }}</span>
                <strong>{{ skippedCount() }}</strong>
              </div>
              <div class="relocation__summary_row">
                <span>{{ 'Lot_Item_Relocation_Destination' | translateWF: 'Destination' }}</span>
                <strong>{{ current.toLocationCode }}</strong>
              </div>
            }
          </section>
        </div>
      </div>
    }
  </div>
</main>
